import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  SportsTennis as SportsIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';

interface AdminAssignment {
  id: string;
  admin_id: string;
  sport: string;
  priority: number;
  max_videos_per_day: number;
  videos_evaluated_today: number;
  timezone: string;
  is_active: boolean;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface AdminUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface PerformanceStats {
  date: string;
  videos_evaluated: number;
  avg_evaluation_time_seconds: number;
  total_evaluation_time_seconds: number;
}

const EvaluationAdminManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [assignments, setAssignments] = useState<AdminAssignment[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<AdminAssignment | null>(null);
  const [activeTab, setActiveTab] = useState<'assignments' | 'performance'>('assignments');
  const [selectedAdminId, setSelectedAdminId] = useState<string>('');
  const [performanceStats, setPerformanceStats] = useState<PerformanceStats[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    adminId: '',
    sport: '',
    priority: 0,
    maxVideosPerDay: 50,
    timezone: 'UTC',
    isActive: true,
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  // Load assignments
  const loadAssignments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/admin/videos/assignments?userId=${user?.id}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setAssignments(data.data || []);
      } else {
        setError(data.message || 'Failed to load assignments');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  // Load evaluation admin users
  const loadAdminUsers = async () => {
    try {
      const response = await fetch(
        `/api/admin/videos/users?role=evaluation_admin&userId=${user?.id}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setAdminUsers(data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load admin users:', err);
    }
  };

  // Load performance stats
  const loadPerformanceStats = async (adminId: string, period: 'daily' | 'weekly' | 'monthly' = 'daily') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        adminId,
        ...(period === 'weekly' && { startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] }),
        ...(period === 'monthly' && { startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] }),
      });
      
      const response = await fetch(
        `/api/admin/videos/stats?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setPerformanceStats(data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load performance stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
    loadAdminUsers();
  }, []);

  const handleOpenDialog = (assignment?: AdminAssignment) => {
    if (assignment) {
      setEditingAssignment(assignment);
      setFormData({
        adminId: assignment.admin_id,
        sport: assignment.sport,
        priority: assignment.priority,
        maxVideosPerDay: assignment.max_videos_per_day,
        timezone: assignment.timezone || 'UTC',
        isActive: assignment.is_active,
      });
    } else {
      setEditingAssignment(null);
      setFormData({
        adminId: '',
        sport: '',
        priority: 0,
        maxVideosPerDay: 50,
        timezone: 'UTC',
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAssignment(null);
  };

  const handleSubmit = async () => {
    if (!formData.adminId || !formData.sport) {
      setError('Admin and Sport are required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/admin/videos/assignments?userId=${user?.id}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            adminId: formData.adminId,
            sport: formData.sport,
            priority: formData.priority,
            maxVideosPerDay: formData.maxVideosPerDay,
            timezone: formData.timezone,
            isActive: formData.isActive,
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Assignment saved successfully');
        handleCloseDialog();
        loadAssignments();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to save assignment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assignmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    setLoading(true);
    try {
      // Note: You may need to add a DELETE endpoint
      const response = await fetch(
        `/api/admin/videos/assignments/${assignmentId}?userId=${user?.id}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Assignment deleted successfully');
        loadAssignments();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to delete assignment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete assignment');
    } finally {
      setLoading(false);
    }
  };

  const sports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Evaluation Admin Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Assignment
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Assignments" value="assignments" />
        <Tab label="Performance Tracking" value="performance" />
      </Tabs>

      {activeTab === 'assignments' ? (
        <Card>
          <CardContent>
            {loading && assignments.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : assignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                No assignments found. Click "Add Assignment" to create one.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Admin</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sport</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Daily Quota</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluated Today</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          {assignment.first_name} {assignment.last_name}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {assignment.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{assignment.sport}</TableCell>
                        <TableCell>{assignment.priority}</TableCell>
                        <TableCell>{assignment.max_videos_per_day}</TableCell>
                        <TableCell>
                          {assignment.videos_evaluated_today} / {assignment.max_videos_per_day}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={assignment.is_active ? 'Active' : 'Inactive'}
                            color={assignment.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenDialog(assignment)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(assignment.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Admin</InputLabel>
                <Select
                  value={selectedAdminId}
                  onChange={(e) => {
                    setSelectedAdminId(e.target.value);
                    if (e.target.value) {
                      loadPerformanceStats(e.target.value);
                    }
                  }}
                  label="Select Admin"
                >
                  <MenuItem value="">Select an admin</MenuItem>
                  {adminUsers.map((admin) => (
                    <MenuItem key={admin.id} value={admin.id}>
                      {admin.first_name} {admin.last_name} ({admin.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {selectedAdminId && performanceStats.length > 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Videos Evaluated</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Avg Time (seconds)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Total Time (hours)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceStats.map((stat) => (
                      <TableRow key={stat.date}>
                        <TableCell>{new Date(stat.date).toLocaleDateString()}</TableCell>
                        <TableCell>{stat.videos_evaluated}</TableCell>
                        <TableCell>{Math.round(stat.avg_evaluation_time_seconds)}</TableCell>
                        <TableCell>{(stat.total_evaluation_time_seconds / 3600).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAssignment ? 'Edit Assignment' : 'Add Assignment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Admin User</InputLabel>
              <Select
                value={formData.adminId}
                onChange={(e) => setFormData({ ...formData, adminId: e.target.value })}
                label="Admin User"
                disabled={!!editingAssignment}
              >
                <MenuItem value="">Select an admin</MenuItem>
                {adminUsers.map((admin) => (
                  <MenuItem key={admin.id} value={admin.id}>
                    {admin.first_name} {admin.last_name} ({admin.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Sport</InputLabel>
              <Select
                value={formData.sport}
                onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                label="Sport"
              >
                {sports.map((sport) => (
                  <MenuItem key={sport} value={sport}>
                    {sport}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2 }}
              helperText="Higher priority admins get videos first"
            />

            <TextField
              fullWidth
              type="number"
              label="Max Videos Per Day"
              value={formData.maxVideosPerDay}
              onChange={(e) => setFormData({ ...formData, maxVideosPerDay: parseInt(e.target.value) || 50 })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Timezone"
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              sx={{ mb: 2 }}
              helperText="e.g., UTC, America/New_York, Asia/Kolkata"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationAdminManagement;

